import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanComparisonTable = ({ currentPlan, onPlanChange }) => {
  const [selectedPlan, setSelectedPlan] = useState(currentPlan);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 49,
      description: 'Perfect for single gym owners',
      features: [
        { name: 'Gym Locations', value: '1 location' },
        { name: 'Members', value: 'Up to 500' },
        { name: 'Staff Accounts', value: 'Up to 5' },
        { name: 'Reporting', value: 'Basic' },
        { name: 'Support', value: 'Email' },
        { name: 'Member Check-in', value: true },
        { name: 'Class Scheduling', value: true },
        { name: 'Equipment Management', value: false },
        { name: 'Financial Reconciliation', value: false },
        { name: 'Multi-location Analytics', value: false },
        { name: 'API Access', value: false }
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 149,
      description: 'For growing gym businesses',
      popular: true,
      features: [
        { name: 'Gym Locations', value: '2-5 locations' },
        { name: 'Members', value: 'Up to 2,000' },
        { name: 'Staff Accounts', value: 'Up to 25' },
        { name: 'Reporting', value: 'Advanced' },
        { name: 'Support', value: 'Priority' },
        { name: 'Member Check-in', value: true },
        { name: 'Class Scheduling', value: true },
        { name: 'Equipment Management', value: true },
        { name: 'Financial Reconciliation', value: true },
        { name: 'Multi-location Analytics', value: true },
        { name: 'API Access', value: false }
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 399,
      description: 'For multi-location gym chains',
      features: [
        { name: 'Gym Locations', value: '6+ locations' },
        { name: 'Members', value: 'Unlimited' },
        { name: 'Staff Accounts', value: 'Unlimited' },
        { name: 'Reporting', value: 'Custom' },
        { name: 'Support', value: 'Dedicated' },
        { name: 'Member Check-in', value: true },
        { name: 'Class Scheduling', value: true },
        { name: 'Equipment Management', value: true },
        { name: 'Financial Reconciliation', value: true },
        { name: 'Multi-location Analytics', value: true },
        { name: 'API Access', value: true }
      ]
    }
  ];

  const handlePlanChange = async () => {
    if (selectedPlan === currentPlan) return;

    setLoading(true);
    try {
      // In production, call API to update subscription
      await new Promise(resolve => setTimeout(resolve, 1500));
      setShowConfirmModal(false);
      onPlanChange?.();
    } catch (error) {
      console.error('Error changing plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateProration = () => {
    const currentPlanData = plans?.find(p => p?.id === currentPlan);
    const newPlanData = plans?.find(p => p?.id === selectedPlan);
    const difference = (newPlanData?.price || 0) - (currentPlanData?.price || 0);
    const daysRemaining = 15; // Mock value
    const proratedAmount = (difference / 30) * daysRemaining;
    return { difference, proratedAmount: Math.abs(proratedAmount)?.toFixed(2), isUpgrade: difference > 0 };
  };

  return (
    <div className="space-y-6">
      {/* Plan Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans?.map((plan) => (
          <div
            key={plan?.id}
            className={`bg-white rounded-lg shadow-sm border-2 transition-all ${
              selectedPlan === plan?.id
                ? 'border-primary shadow-lg scale-105'
                : 'border-gray-200 hover:border-gray-300'
            } ${
              plan?.popular ? 'relative' : ''
            }`}
          >
            {plan?.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-primary text-white px-4 py-1 rounded-full text-xs font-semibold">
                  Most Popular
                </span>
              </div>
            )}

            <div className="p-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{plan?.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{plan?.description}</p>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold text-gray-900">${plan?.price}</span>
                  <span className="text-gray-600">/month</span>
                </div>
              </div>

              {currentPlan === plan?.id && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                  <span className="text-sm font-medium text-green-800">
                    <Icon name="Check" className="w-4 h-4 inline mr-1" />
                    Current Plan
                  </span>
                </div>
              )}

              <Button
                fullWidth
                variant={selectedPlan === plan?.id ? 'default' : 'outline'}
                onClick={() => setSelectedPlan(plan?.id)}
                disabled={currentPlan === plan?.id}
              >
                {currentPlan === plan?.id ? 'Current Plan' : 'Select Plan'}
              </Button>

              <div className="mt-6 space-y-3">
                {plan?.features?.slice(0, 5)?.map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    {typeof feature?.value === 'boolean' ? (
                      feature?.value ? (
                        <Icon name="Check" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <Icon name="X" className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      )
                    ) : (
                      <Icon name="Check" className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">{feature?.name}:</span>
                      {typeof feature?.value !== 'boolean' && (
                        <span className="text-gray-600 ml-1">{feature?.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Feature Comparison Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Detailed Feature Comparison</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Feature
                </th>
                {plans?.map((plan) => (
                  <th key={plan?.id} className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {plan?.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {plans?.[0]?.features?.map((_, featureIndex) => (
                <tr key={featureIndex}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {plans?.[0]?.features?.[featureIndex]?.name}
                  </td>
                  {plans?.map((plan) => (
                    <td key={plan?.id} className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      {typeof plan?.features?.[featureIndex]?.value === 'boolean' ? (
                        plan?.features?.[featureIndex]?.value ? (
                          <Icon name="Check" className="w-5 h-5 text-green-600 mx-auto" />
                        ) : (
                          <Icon name="X" className="w-5 h-5 text-gray-400 mx-auto" />
                        )
                      ) : (
                        <span className="text-gray-900">{plan?.features?.[featureIndex]?.value}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Change Plan Button */}
      {selectedPlan !== currentPlan && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {calculateProration()?.isUpgrade ? 'Upgrade' : 'Downgrade'} to {plans?.find(p => p?.id === selectedPlan)?.name}
              </h3>
              <p className="text-sm text-gray-600">
                {calculateProration()?.isUpgrade ? 'Immediate upgrade' : 'Changes take effect next billing cycle'}
              </p>
            </div>
            <Button
              size="lg"
              onClick={() => setShowConfirmModal(true)}
              iconName={calculateProration()?.isUpgrade ? 'ArrowUp' : 'ArrowDown'}
            >
              Change Plan
            </Button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="AlertCircle" className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Confirm Plan Change
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  You are about to {calculateProration()?.isUpgrade ? 'upgrade' : 'downgrade'} from{' '}
                  <span className="font-semibold">{plans?.find(p => p?.id === currentPlan)?.name}</span> to{' '}
                  <span className="font-semibold">{plans?.find(p => p?.id === selectedPlan)?.name}</span>.
                </p>
                {calculateProration()?.isUpgrade && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                    <p className="text-sm text-blue-900">
                      <span className="font-semibold">Prorated charge:</span> ${calculateProration()?.proratedAmount} will be charged today for the remaining billing period.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                fullWidth
                variant="outline"
                onClick={() => setShowConfirmModal(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                onClick={handlePlanChange}
                loading={loading}
              >
                Confirm Change
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanComparisonTable;